/**
 * Artboard → output fitting for the video compositor. Pure.
 *
 * `cover` fills the frame and crops the overflow; it is the default because a
 * 1080×1920 artboard exported at 1080×1920 (or close to it) should never show
 * bars. When the aspect ratios differ by more than FIT_MISMATCH_TOLERANCE,
 * cropping would cut real content, so we `contain` and letterbox with the
 * scene's bgColor instead.
 */

export type FitMode = 'cover' | 'contain';

export interface Size {
  width: number;
  height: number;
}

export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** Aspect mismatch (relative) beyond which cover would crop too much. */
export const FIT_MISMATCH_TOLERANCE = 0.1;

export function fitModeFor(artboard: Size, out: Size): FitMode {
  const a = artboard.width / Math.max(1, artboard.height);
  const o = out.width / Math.max(1, out.height);
  return Math.abs(a / o - 1) > FIT_MISMATCH_TOLERANCE ? 'contain' : 'cover';
}

/** Scale factor from artboard to output for `mode`. */
export function fitScale(artboard: Size, out: Size, mode: FitMode): number {
  const sx = out.width / Math.max(1, artboard.width);
  const sy = out.height / Math.max(1, artboard.height);
  return mode === 'cover' ? Math.max(sx, sy) : Math.min(sx, sy);
}

/** Where the scaled artboard lands inside the output, centered. */
export function fitRect(artboard: Size, out: Size, mode: FitMode): Rect {
  const s = fitScale(artboard, out, mode);
  const width = artboard.width * s;
  const height = artboard.height * s;
  return { x: (out.width - width) / 2, y: (out.height - height) / 2, width, height };
}
